import { useCallback, useRef } from 'react';

/**
 * Shares a pending promise between repeated runs of the same effect. The returned
 * `run(key, start)` calls `start()` unless a promise started under the same key is
 * still pending, in which case that promise is returned instead of a new one.
 *
 * StrictMode mounts, unmounts and mounts an effect again before its first request
 * can settle. An effect that awaits `run(...)` sends one request per mount: the
 * second run awaits what the first one started, and its own continuation is the
 * one that commits the result.
 */
export default function useInFlightPromise<T>(): (
	key: string,
	start: () => Promise<T>
) => Promise<T> {
	const inFlightRef = useRef(new Map<string, Promise<T>>());

	return useCallback((key, start) => {
		const inFlight = inFlightRef.current.get(key);
		if (inFlight) {
			return inFlight;
		}
		const promise = start();
		inFlightRef.current.set(key, promise);
		const release = () => {
			if (inFlightRef.current.get(key) === promise) {
				inFlightRef.current.delete(key);
			}
		};
		promise.then(release, release);
		return promise;
	}, []);
}
